import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { ENUM__local_event } from '@/services/events/ENUM__local_event';
import { service_local_events } from '@/services/events/service_local_events';
import { remote_workspace_child_entity_viewer_action } from '@/state/workspace/child_entity/remote_workspace_child_entity_viewer_action';
import { set_timeout } from '@/utils/utils_client';
import { get_entity_viewer_color_as_workspace_user } from '@/utils/utils_client_workspace_child_entity_viewers';
import { RequestViewerAction } from '@shared/common/_requests/request_viewer_action';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { timestamp_ms } from '@shared/utils/utils_time';
import { useRef, useState } from 'react';
import { use_dispatch } from './_base/use_dispatch';
import { use_effect } from './_base/use_effect';
import { use_slice_workspace } from './store/use_slices';

export interface HOOK_RESULT__notifier {
  is_blinking: boolean;
  color_index: string;
  send_action: () => Promise<void>;
}

// info: for border blink use NotifierBorder component (parent component must have className="relative")
export function HOOK__notifier(component_id: string, entity_type: string = ''): HOOK_RESULT__notifier {
  const dispatch = use_dispatch();
  const { current_viewed_entity, child_entity_viewers_map, all_workspaces_users_map, workspace_current, current_workspace_user } = use_slice_workspace([
    'current_viewed_entity',
    'child_entity_viewers_map',
    'all_workspaces_users_map',
    'workspace_current',
    'current_workspace_user',
  ]);

  const ref_timeout_id_blinking = useRef(NaN);

  const [is_blinking, set_is_blinking] = useState(false);
  const [color_index, set_color_index] = useState('0');

  /*
   * EFFECTS
   */

  use_effect(() => {
    return () => stop_blinking();
  }, [ref_timeout_id_blinking.current]);

  use_effect(() => {
    service_local_events.on(ENUM__local_event.RESPONSE_VIEWER_ACTION_RECEIVED, on_event_from_other_user_received);

    return () => service_local_events.off(ENUM__local_event.RESPONSE_VIEWER_ACTION_RECEIVED, on_event_from_other_user_received);
  }, [on_event_from_other_user_received]);

  /*
   * EVENTS
   */

  function on_event_from_other_user_received(data: RequestViewerAction) {
    if (data.component_id !== component_id) return;

    let current_time = timestamp_ms();
    let time_diff = current_time - data.created_at;

    if (time_diff > CONSTANTS__client.NOTIFIER_REQUEST_TIMEOUT) return;

    let entity_viewers = Array.from(child_entity_viewers_map.values()).filter(
      viewer => viewer.entity_id === current_viewed_entity._id && viewer.entity_type === current_viewed_entity.type,
    );

    let viewer_entity = entity_viewers.find(viewer => viewer.get_viewer_id() === data.viewer_id);
    if (!viewer_entity) return;

    let workspace_users_map = all_workspaces_users_map.get_or_create_map(workspace_current._id);

    let all_viewer_user_ids = Array.from(new Set([...entity_viewers.map(v => v.user_id), current_workspace_user.user_id]));
    let workspace_viewer_ids = all_viewer_user_ids.filter(id => workspace_users_map.has(id)).sort((a, b) => b.localeCompare(a));

    if (entity_type) {
      set_color_index(get_entity_viewer_color_as_workspace_user(viewer_entity, workspace_viewer_ids));
    } else {
      set_color_index(CONSTANTS__client.USER_NEUTRAL_COLOR);
    }

    start_blinking();
  }

  /*
   * METHODS
   */

  function start_blinking() {
    stop_blinking();

    set_is_blinking(true);

    ref_timeout_id_blinking.current = set_timeout(() => set_is_blinking(false), CONSTANTS_TIME__client.TIMEOUT_DURATION_ANIMATION_NOTIFIER_BLINK);
  }

  function stop_blinking() {
    clearTimeout(ref_timeout_id_blinking.current);
    ref_timeout_id_blinking.current = NaN;
    set_is_blinking(false);
  }

  async function send_action() {
    if (!current_workspace_user._id) return;

    const action_data = new RequestViewerAction();
    action_data.viewer_id = current_workspace_user._id || current_workspace_user.user_id;
    action_data.workspace_id = current_viewed_entity.workspace_id;
    action_data.workspace_id = workspace_current._id;

    if (current_viewed_entity.is_stub()) {
      action_data.current_viewed_entity_id = ENUM__string_id_empty.OBJECT_ID;
    } else {
      action_data.current_viewed_entity_id = current_viewed_entity._id;
    }

    action_data.component_id = component_id;
    action_data.entity_type = entity_type;

    action_data.created_at = timestamp_ms();

    await dispatch(remote_workspace_child_entity_viewer_action(action_data));
  }

  return {
    is_blinking,
    color_index,
    send_action,
  };
}
